<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Vault') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('vault')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="refreshItems"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <aside
                    :aria-hidden="!filterSidebarOpen"
                    class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-40"
                    :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                >
                    <div class="stage-filter-body">
                        <div class="stage-filter-title-row">
                            <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                            <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                        </div>
                        <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                        <div class="stage-filter-row mb-5">
                            <button
                                class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                type="button"
                                @click="toggleSort"
                            >
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                            </button>
                            <div class="stage-filter-sort-select-wrap">
                                <select
                                    v-model="sort.column"
                                    class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                    @change="onFilterChange"
                                >
                                    <option value="updated_at">Letztes Update</option>
                                    <option value="title">Titel</option>
                                    <option value="owner">Erstellt von</option>
                                </select>
                                <span class="stage-filter-select-icon">
                                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                </span>
                            </div>
                        </div>
                        <div class="stage-filter-label">{{ $t('Search') }}</div>
                        <div class="mb-4">
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </div>
                                <input
                                    v-model.lazy="filters.search"
                                    :placeholder="$t('Search')"
                                    class="stage-filter-input form-input block pl-10 text-sm"
                                    @change="onFilterChange"
                                >
                            </div>
                        </div>
                        <div v-if="hasOwnerFilterEntries" class="stage-filter-label">{{ $t('Created by') }}</div>
                        <div
                            v-if="hasOwnerFilterEntries"
                            class="mb-4 border border-bordergray-100 rounded bg-white px-3 py-2 max-h-[7.5rem] overflow-y-auto overflow-x-hidden"
                        >
                            <label
                                class="flex items-center text-sm mb-2 last:mb-0 cursor-pointer"
                            >
                                <input
                                    :checked="!ownerScope"
                                    class="form-radio h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-2"
                                    type="radio"
                                    name="vault-owner-filter"
                                    @change="setOwnerFilter(null)"
                                >
                                <span class="text-sm text-gray-700 truncate">{{ $t('All users') }}</span>
                            </label>
                            <label
                                v-for="owner in ownerFilterOptions"
                                :key="'vault-sidebar-owner-' + owner.id"
                                class="flex items-center text-sm mb-2 last:mb-0 cursor-pointer"
                            >
                                <input
                                    :checked="isOwnerSelected(owner.id)"
                                    class="form-radio h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-2"
                                    type="radio"
                                    name="vault-owner-filter"
                                    @change="setOwnerFilter(owner.id)"
                                >
                                <img
                                    :src="owner.avatarUrl"
                                    :alt="owner.name"
                                    class="h-5 w-5 rounded-full object-cover mr-2 flex-shrink-0"
                                >
                                <span class="text-sm text-gray-700 truncate">{{ owner.name }}</span>
                                <span class="ml-auto text-sm text-gray-500">{{ owner.count }}</span>
                            </label>
                        </div>
                        <div v-if="categories.length > 0" class="stage-filter-label">Kategorien</div>
                        <div v-if="categories.length > 0" class="mb-3 border border-bordergray-100 rounded bg-white px-3 py-2 max-h-52 overflow-auto">
                            <label
                                v-for="category in categories"
                                :key="'vault-sidebar-category-' + category.id"
                                class="flex items-center text-sm mb-2 last:mb-0 cursor-pointer"
                            >
                                <input
                                    :checked="isCategorySelected(category.id)"
                                    class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-2"
                                    type="checkbox"
                                    @change="toggleCategoryFilter(category.id)"
                                >
                                <span class="text-sm text-gray-700 truncate">{{ category.name }}</span>
                                <span class="h-2.5 w-2.5 rounded-full ml-2 mr-2 flex-shrink-0" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                <span class="ml-auto text-sm text-gray-500">{{ category.files_count || 0 }}</span>
                            </label>
                        </div>
                        <div class="stage-filter-label">{{ $t('Items per page') }}</div>
                        <div class="mb-3">
                            <input-select
                                id="vault-per-page"
                                v-model="perPage"
                                class="stage-filter-select"
                                :options="perPageOptions"
                                option-label="name"
                                @change="onPerPageChange"
                            />
                        </div>
                        <div v-if="hasActiveFilters">
                            <button
                                id="vault-filter-reset-btn"
                                type="button"
                                class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                @click="resetFilters"
                            >
                                {{ $t('Reset filters') }}
                            </button>
                        </div>
                    </div>
                </aside>

                <div class="border-b border-ultralight">
                    <div class="stage-header-surface">
                        <div class="flex items-stretch min-h-[52px]">
                            <div class="inline-flex items-center px-4 border-r border-ultralight">
                                <button
                                    :class="viewMode === 'list' ? 'vault-view-btn-active' : ''"
                                    class="vault-view-btn"
                                    type="button"
                                    @click="viewMode = 'list'"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.bars"></svg-vue>
                                </button>
                                <button
                                    :class="viewMode === 'grid' ? 'vault-view-btn-active' : ''"
                                    class="vault-view-btn ml-2"
                                    type="button"
                                    @click="viewMode = 'grid'"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.grid-2"></svg-vue>
                                </button>
                            </div>
                            <div class="ml-auto inline-flex items-stretch">
                                <div
                                    v-if="canManageCategories"
                                    v-on-clickaway="closeCategoryManager"
                                    class="vault-header-category-cell border-l border-ultralight relative"
                                >
                                    <button
                                        type="button"
                                        class="vault-header-category-btn w-full h-full text-gray-700"
                                        @click="toggleCategoryManager"
                                    >
                                        <svg-vue class="h-4 w-4 vault-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                    </button>
                                    <div v-show="categoryManagerOpen" class="origin-top-right z-40 absolute right-0 top-full mt-2 w-64 rounded-sm border border-bordergray-100 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)] overflow-hidden">
                                        <div class="flex items-stretch w-full border-b border-bordergray-100">
                                            <category-create-input
                                                ref="categoryManagerInput"
                                                v-model:color="categoryDraft.color"
                                                v-model:name="categoryDraft.name"
                                                :maxlength="120"
                                                :placeholder="$t('Color and category').toString()"
                                                @submit="createCategory"
                                            />
                                        </div>
                                        <div v-for="category in categories" :key="'vault-manager-category-' + category.id" class="vault-category-row border-b border-bordergray-100 last:border-b-0 flex items-center">
                                            <div class="vault-category-row-content inline-flex items-center min-w-0 flex-1">
                                                <span class="vault-category-row-dot rounded-full flex-shrink-0" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                <span class="text-sm text-gray-700 truncate">{{ category.name }}</span>
                                                <span class="text-sm text-gray-500 ml-2">({{ category.files_count || 0 }})</span>
                                            </div>
                                            <div class="vault-category-row-actions">
                                                <button
                                                    v-if="canManageCategory(category)"
                                                    class="vault-category-delete-btn text-gray-500 hover:text-gray-700 focus:outline-none inline-flex items-center justify-center"
                                                    type="button"
                                                    @click.stop="deleteCategory(category)"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="w-32 border-l border-ultralight">
                                    <button
                                        class="btn-stage-primary w-full h-full border-0 cursor-pointer"
                                        style="border-radius: 0;"
                                        type="button"
                                        @click="$router.push(vaultNewPath)"
                                    >
                                        {{ $t('New note') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <template v-if="viewMode === 'list'">
                            <div class="hidden sm:block h-full" v-if="paginatedItems.length > 0">
                                <div class="h-full overflow-auto">
                                    <table class="table-listview table-listview--zebra vault-list-table">
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th vault-list-sticky-th">Titel</th>
                                            <th class="table-listview-l-th vault-list-sticky-th">Erstellt von</th>
                                            <th class="table-listview-l-th vault-list-sticky-th">Letztes Update</th>
                                            <th class="table-listview-l-th vault-list-sticky-th">Erstellt am</th>
                                            <th class="table-listview-l-th table-listview-l-th-right vault-list-sticky-th w-24">Aktionen</th>
                                        </tr>
                                        </thead>
                                        <tbody class="bg-white">
                                        <tr
                                            v-for="item in paginatedItems"
                                            :key="'list-' + item.id"
                                            class="cursor-pointer"
                                            @click="openVaultEntry(item)"
                                        >
                                            <td class="table-listview-l-td whitespace-no-wrap">
                                                <div class="flex items-center text-sm font-medium text-gray-900 min-w-0">
                                                    <span class="vault-icon-box mr-3">
                                                        <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.light.table-cells-row-lock"></svg-vue>
                                                    </span>
                                                    <span class="truncate block">{{ item.title }}</span>
                                                    <span v-if="item.category" class="vault-list-category-dot ml-2 flex-shrink-0" :style="{ backgroundColor: getCategoryColor(item) }"></span>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700">
                                                <div class="flex items-center min-w-0">
                                                    <img
                                                        :src="ownerAvatar(item)"
                                                        :alt="item.owner"
                                                        class="h-5 w-5 rounded-full mr-2 object-cover"
                                                    >
                                                    <span class="truncate block">{{ item.owner }}</span>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-500">
                                                <div class="flex items-center min-w-0">
                                                    <img
                                                        :src="ownerAvatar(item)"
                                                        :alt="item.owner"
                                                        class="h-5 w-5 rounded-full mr-2 object-cover"
                                                    >
                                                    <span class="truncate block">{{ $momentFormatDateTimeAgo(item.updated_at) }}</span>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-500">{{ $momentFormatDateTimeAgo(item.created_at) }}</td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-right">
                                                <button
                                                    v-if="item.can_delete"
                                                    class="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                                    type="button"
                                                    title="Notiz löschen"
                                                    aria-label="Notiz löschen"
                                                    @click.stop="requestDelete(item)"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                </button>
                                                <span v-else class="text-sm text-gray-400">-</span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <div v-if="paginatedItems.length > 0" class="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 vault-tile-grid">
                                <div
                                    v-for="item in paginatedItems"
                                    :key="'grid-' + item.id"
                                    class="bg-white border border-bordergray-100 rounded vault-tile-item block"
                                    :style="{ borderLeftColor: item.category ? getCategoryColor(item) : undefined, borderLeftWidth: item.category ? '4px' : undefined }"
                                    @click="openVaultEntry(item)"
                                >
                                    <div v-if="canManageCategories && item.can_edit && categories.length > 0" v-on-clickaway="closeItemCategoryMenu" class="absolute left-3 top-3 inline-flex" @click.stop>
                                        <button
                                            type="button"
                                            class="inline-flex items-center justify-center text-gray-700"
                                            @click.stop="toggleItemCategoryMenu(item)"
                                        >
                                            <svg-vue class="h-4 w-4 vault-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                        </button>
                                        <div v-show="isItemCategoryMenuOpen(item)" class="origin-top-left z-50 absolute left-0 top-full mt-2 w-52 rounded border border-bordergray-100 bg-white">
                                            <button
                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                type="button"
                                                @click.stop="assignCategory(item, null)"
                                            >
                                                Keine Kategorie
                                            </button>
                                            <button
                                                v-for="category in categories"
                                                :key="'vault-assign-category-grid-' + item.id + '-' + category.id"
                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                type="button"
                                                @click.stop="assignCategory(item, category.id)"
                                            >
                                                <span class="inline-flex items-center">
                                                    <span class="h-2.5 w-2.5 rounded-full mr-2" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                    <span>{{ category.name }}</span>
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="px-6 py-6 text-center vault-tile-header">
                                        <div class="mx-auto mb-4 flex items-center justify-center">
                                            <svg-vue class="h-16 w-16 text-gray-400" icon="font-awesome.light.table-cells-row-lock"></svg-vue>
                                        </div>
                                        <div class="vault-tile-title-row">
                                            <span class="vault-tile-title-text">{{ item.title }}</span>
                                            <span class="inline-flex items-center ml-1" @click.stop>
                                                <app-tooltip
                                                    placement="bottom"
                                                    width-class="w-max max-w-xs"
                                                    class="inline-flex items-center vault-title-tooltip"
                                                >
                                                    <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                    <template #content>
                                                        <div class="vault-info-tooltip-content">
                                                            {{ shortDescriptionTooltip(item.short_description) }}
                                                        </div>
                                                    </template>
                                                </app-tooltip>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="px-4 border-t border-bordergray-100 flex items-center h-12 text-xs text-gray-500" @click.stop>
                                        <div class="min-w-0 flex items-center">
                                            <img
                                                :src="ownerAvatar(item)"
                                                :alt="item.owner"
                                                class="h-6 w-6 rounded-full object-cover mr-2"
                                            >
                                            <span class="truncate">{{ $momentFormatDateTimeAgo(item.updated_at) }}</span>
                                        </div>
                                        <button
                                            v-if="item.can_delete"
                                            class="ml-auto inline-flex items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                            type="button"
                                            title="Notiz löschen"
                                            aria-label="Notiz löschen"
                                            @click.stop="requestDelete(item)"
                                        >
                                            <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template v-if="paginatedItems.length < 1 && !loading">
                            <empty-state />
                        </template>
                    </div>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    nav-class="relative z-30"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>

        <app-dialog
            :open="unlockModalOpen"
            title="Einmal Code eingeben"
            size="sm"
            :busy="unlockSubmitting"
            @close="closeUnlockModal"
        >
            <div class="vault-otp-group" @paste="handleCodePaste">
                <input
                    v-for="(digit, index) in unlockCodeDigits"
                    :key="'unlock-digit-' + index"
                    ref="unlockCodeInput"
                    v-model="unlockCodeDigits[index]"
                    autocomplete="one-time-code"
                    inputmode="numeric"
                    maxlength="1"
                    type="text"
                    class="vault-otp-input"
                    :disabled="unlockSubmitting"
                    @input="handleCodeInput(index, $event)"
                    @keydown="handleCodeKeydown(index, $event)"
                >
            </div>
            <p class="mt-2 text-xs text-gray-500" aria-live="polite">
                Der Code ist 10 Minuten gültig<span v-if="unlockCodeCooldownRemaining > 0"> und kann in {{ unlockCodeCooldownRemaining }} Sek. neu angefordert werden</span>.
            </p>
            <div v-if="unlockError" class="mt-3 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded text-left">
                {{ unlockError }}
            </div>

            <template #footer>
                <div class="flex min-w-0 flex-1 items-center px-6">
                    <img :src="currentUserAvatar" alt="Profilbild" class="h-6 w-6 rounded-full object-cover mr-2 flex-shrink-0">
                    <div class="min-w-0 truncate text-xs text-gray-500">{{ currentUserEmail || 'eingeloggter Nutzer' }}</div>
                </div>
                <app-dialog-action
                    label="Code erhalten"
                    :disabled="sendingUnlockCode || unlockCodeCooldownRemaining > 0"
                    @click="sendUnlockCodeByEmail"
                />
            </template>
        </app-dialog>

        <confirm-dialog
            :open="deleteNoteModalOpen"
            :title="$t('Delete note').toString()"
            :text="$t('Do you really want to delete this note? This action cannot be undone.').toString()"
            confirm-label="Löschen"
            confirm-variant="danger"
            headline-id="vault-list-delete-note-modal-headline"
            @close="closeDeleteModal"
            @confirm="deleteSelectedVault"
        />
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import CategoryCreateInput from "@/components/forms/category-create-input.vue";
import AppDialog from "@/components/modals/app-dialog.vue";
import AppDialogAction from "@/components/modals/app-dialog-action.vue";
import ConfirmDialog from "@/components/modals/confirm-dialog.vue";
import {resolveAccessState} from "@/utils/access-state";
import {UI_COLORS} from '@/utils/ui-colors';
import {
    queryNumber,
    queryNumberArray,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";
import {canFilterVaultOwners, canManageVaultCategories} from "@/utils/sharing-access";

export default {
    name: "dashboard-vault-list",
    components: {
        CategoryCreateInput,
        AppDialog,
        AppDialogAction,
        ConfirmDialog,
    },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.$t('Vault')
        };
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const allowedSortColumns = ['updated_at', 'title', 'owner'];
        const initialSortColumn = queryString(routeQuery, 'list_sort_column', 'updated_at');
        const initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc') === 'asc' ? 'asc' : 'desc';
        const initialOwnerScope = queryNumber(routeQuery, 'list_owner_id', 0);

        return {
            page: queryNumber(routeQuery, 'list_page', 1),
            loading: true,
            filterSidebarOpen: false,
            viewMode: 'grid',
            items: [],
            ownerCounters: [],
            ownerScope: initialOwnerScope > 0 ? initialOwnerScope : null,
            pagination: {
                currentPage: 1,
                perPage: 25,
                total: 0,
                totalPages: 1,
            },
            unlockModalOpen: false,
            deleteNoteModalOpen: false,
            selectedItem: null,
            pendingDeleteItem: null,
            unlockCodeDigits: ['', '', '', ''],
            unlockError: '',
            nowTick: Date.now(),
            unlockCodeCooldownUntil: 0,
            sendingUnlockCode: false,
            unlockSubmitting: false,
            activeVerifyRequestSeq: 0,
            verifyRequestSeq: 0,
            clockIntervalId: null,
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
            },
            sort: {
                column: allowedSortColumns.includes(initialSortColumn) ? initialSortColumn : 'updated_at',
                order: initialSortOrder,
            },
            perPage: queryNumber(routeQuery, 'list_per_page', 25),
            categories: [],
            selectedCategoryIds: queryNumberArray(routeQuery, 'list_categories'),
            categoryManagerOpen: false,
            openCategoryMenu: null,
            categoryDraft: {
                name: '',
                color: UI_COLORS.text.subtle,
            },
        };
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state ? this.$store.state.user : null;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        canFilterByOwners() {
            return canFilterVaultOwners(this.accessState, this.currentUser);
        },
        canManageCategories() {
            return canManageVaultCategories(this.accessState, this.currentUser);
        },
        hasOwnerFilterEntries() {
            return this.canFilterByOwners && this.ownerFilterOptions.length > 0;
        },
        ownerFilterOptions() {
            return (this.ownerCounters || []).map((owner) => ({
                id: Number(owner.id),
                name: owner.name || 'Unbekannt',
                count: Number(owner.count || 0),
                avatarUrl: owner.avatar && owner.avatar !== 'gravatar' ? owner.avatar : owner.gravatar,
            }));
        },
        vaultBasePath() {
            return this.$route.path.startsWith('/workspace/vault') ? '/workspace/vault' : '/vault';
        },
        apiVaultBasePath() {
            return this.$route.path.startsWith('/workspace/vault') ? 'api/workspace/vault' : 'api/vault';
        },
        vaultListPath() {
            return this.vaultBasePath === '/workspace/vault' ? '/workspace/vault' : '/vault/list';
        },
        vaultNewPath() {
            return this.vaultBasePath + '/new';
        },
        paginatedItems() {
            return this.items;
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        perPageOptions() {
            const defaults = [10, 25, 50, 100];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || this.sort.order !== 'desc'
                || this.sort.column !== 'updated_at'
                || Number(this.perPage || 0) !== 25
                || Number(this.ownerScope || 0) > 0
                || this.selectedCategoryIds.length > 0;
        },
        currentUserEmail() {
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            return user && user.email ? String(user.email) : '';
        },
        currentUserAvatar() {
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            if (!user) {
                return 'https://i.pravatar.cc/64?img=1';
            }

            if (user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }

            if (user.gravatar) {
                return user.gravatar;
            }

            return 'https://i.pravatar.cc/64?img=1';
        },
        unlockCodeCooldownRemaining() {
            if (!this.unlockCodeCooldownUntil) {
                return 0;
            }
            const remainingMs = this.unlockCodeCooldownUntil - this.nowTick;
            if (remainingMs <= 0) {
                return 0;
            }
            return Math.ceil(remainingMs / 1000);
        },
    },
    mounted() {
        this.refreshItems();
        this.clockIntervalId = window.setInterval(() => {
            this.nowTick = Date.now();
        }, 1000);
    },
    beforeUnmount() {
        if (this.clockIntervalId) {
            window.clearInterval(this.clockIntervalId);
            this.clockIntervalId = null;
        }
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        refreshItems() {
            const self = this;
            self.syncVaultListRouteState();
            self.loading = true;
            axios.get(self.apiVaultBasePath, {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search,
                    owner_id: self.ownerScope,
                    category_ids: self.selectedCategoryIds,
                }
            }).then(function (response) {
                self.items = response.data.items || [];
                self.categories = response.data.categories || self.categories;
                self.ownerCounters = response.data.owner_counters || [];
                self.pagination = response.data.pagination || self.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage && self.pagination.totalPages > 0) {
                    self.page = self.pagination.totalPages;
                    self.refreshItems();
                    return;
                }
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        syncVaultListRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_owner_id',
                'list_categories',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', Number(this.page || 1) === 1 ? null : Number(this.page || 1));
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 0) === 25 ? null : Number(this.perPage || 0));
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column === 'updated_at' ? null : this.sort.column);
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order === 'desc' ? null : this.sort.order);
            setQueryValue(nextQuery, 'list_search', (this.filters.search || '').trim() || null);
            setQueryValue(nextQuery, 'list_owner_id', Number(this.ownerScope || 0) > 0 ? Number(this.ownerScope) : null);
            setQueryValue(nextQuery, 'list_categories', this.selectedCategoryIds.length > 0 ? this.selectedCategoryIds : null);

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        onFilterChange() {
            this.page = 1;
            this.refreshItems();
        },
        onPerPageChange() {
            this.page = 1;
            this.refreshItems();
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        toggleSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.page = 1;
            this.refreshItems();
        },
        isOwnerSelected(ownerId) {
            return Number(this.ownerScope || 0) === Number(ownerId);
        },
        setOwnerFilter(ownerId) {
            const id = Number(ownerId || 0);
            this.ownerScope = id > 0 ? id : null;
            this.page = 1;
            this.refreshItems();
        },
        isCategorySelected(categoryId) {
            return this.selectedCategoryIds.includes(Number(categoryId));
        },
        toggleCategoryFilter(categoryId) {
            const id = Number(categoryId);
            if (id < 1) {
                return;
            }

            if (this.selectedCategoryIds.includes(id)) {
                this.selectedCategoryIds = this.selectedCategoryIds.filter((selectedId) => selectedId !== id);
            } else {
                this.selectedCategoryIds = [...this.selectedCategoryIds, id];
            }

            this.page = 1;
            this.refreshItems();
        },
        toggleCategoryManager() {
            if (!this.canManageCategories) {
                return;
            }
            const shouldOpen = !this.categoryManagerOpen;
            this.categoryManagerOpen = shouldOpen;
            this.closeItemCategoryMenu();
            if (shouldOpen) {
                this.focusCategoryManagerInput();
            }
        },
        closeCategoryManager() {
            this.categoryManagerOpen = false;
        },
        focusCategoryManagerInput() {
            this.$nextTick(() => {
                const input = this.$refs.categoryManagerInput;
                if (input && typeof input.focusInput === 'function') {
                    input.focusInput();
                } else if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            });
        },
        canManageCategory(category) {
            if (!category) {
                return false;
            }

            const user = this.currentUser;
            if (!user) {
                return false;
            }

            return this.accessState.isAdmin || Number(category.created_by_user_id || 0) === Number(user.id || 0);
        },
        createCategory() {
            if (!this.canManageCategories) {
                return;
            }
            const self = this;
            const name = String(self.categoryDraft.name || '').trim();
            if (!name) {
                if (self.$notify) {
                    self.$notify({
                        title: self.$t('Error').toString(),
                        text: self.$t('Choose a color and a category text').toString(),
                        type: 'error',
                    });
                }
                self.$nextTick(function () {
                    const input = self.$refs.categoryManagerInput;
                    if (input && typeof input.focusInput === 'function') {
                        input.focusInput();
                    } else if (input && typeof input.focus === 'function') {
                        input.focus();
                    }
                });
                return;
            }

            axios.post(self.apiVaultBasePath + '/categories', {
                name: name,
                color: self.categoryDraft.color || UI_COLORS.text.subtle,
                scope: 'vault',
            }).then(function (response) {
                self.categories = response.data.categories || self.categories;
                self.categoryDraft.name = '';
                self.categoryDraft.color = UI_COLORS.text.subtle;
                self.refreshItems();
            });
        },
        deleteCategory(category) {
            if (!this.canManageCategories) {
                return;
            }
            const self = this;
            if (!category || !category.id) {
                return;
            }

            axios.delete(self.apiVaultBasePath + '/categories/' + category.id).then(function (response) {
                const deletedCategoryId = Number(category.id);
                self.categories = response.data.categories || [];
                self.selectedCategoryIds = self.selectedCategoryIds.filter((id) => id !== deletedCategoryId);
                self.refreshItems();
            });
        },
        categoryMenuKey(item) {
            return `vault-category-${item.id}`;
        },
        isItemCategoryMenuOpen(item) {
            return this.openCategoryMenu === this.categoryMenuKey(item);
        },
        toggleItemCategoryMenu(item) {
            if (!this.canManageCategories) {
                return;
            }
            const key = this.categoryMenuKey(item);
            this.categoryManagerOpen = false;
            this.openCategoryMenu = this.openCategoryMenu === key ? null : key;
        },
        closeItemCategoryMenu() {
            this.openCategoryMenu = null;
        },
        assignCategory(item, categoryId) {
            if (!this.canManageCategories || !item || !item.can_edit) {
                return;
            }

            const self = this;
            axios.patch(self.apiVaultBasePath + '/' + item.id + '/category', {
                category_id: categoryId || null,
            }).then(function (response) {
                const selectedCategory = categoryId
                    ? self.categories.find((category) => Number(category.id) === Number(categoryId))
                    : null;
                item.category_id = categoryId ? Number(categoryId) : null;
                item.category = selectedCategory ? {
                    id: Number(selectedCategory.id),
                    name: selectedCategory.name,
                    color: selectedCategory.color,
                } : null;
                if (Array.isArray(response.data.categories)) {
                    self.categories = response.data.categories;
                }
                self.closeItemCategoryMenu();
            });
        },
        getCategoryColor(item) {
            return item && item.category && item.category.color ? item.category.color : UI_COLORS.border.default;
        },
        resetFilters() {
            this.filters.search = '';
            this.sort.order = 'desc';
            this.sort.column = 'updated_at';
            this.perPage = 25;
            this.ownerScope = null;
            this.selectedCategoryIds = [];
            this.page = 1;
            this.refreshItems();
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.refreshItems();
            }
        },
        ownerAvatar(item) {
            if (item && item.owner_avatar) {
                return item.owner_avatar;
            }
            return 'https://i.pravatar.cc/64?img=1';
        },
        itemManageLink(item) {
            return routeWithReturnTo(this.vaultBasePath + '/' + item.id + '/manage', this.$route, this.vaultListPath);
        },
        isItemUnlocked(item) {
            return !!(item && item.is_unlocked);
        },
        openVaultEntry(item) {
            if (!item || !item.id) {
                return;
            }

            if (this.isItemUnlocked(item)) {
                this.$router.push(this.itemManageLink(item));
                return;
            }

            this.openUnlockModal(item);
        },
        shortDescriptionTooltip(shortDescription) {
            const normalized = String(shortDescription || '').replace(/\s+/g, ' ').trim();
            return normalized || 'Keine Kurzbeschreibung hinterlegt.';
        },
        openUnlockModal(item) {
            this.selectedItem = item || null;
            this.unlockCodeDigits = ['', '', '', ''];
            this.unlockError = '';
            this.unlockModalOpen = true;
            this.$nextTick(() => {
                const inputs = this.$refs.unlockCodeInput || [];
                if (inputs[0]) {
                    inputs[0].focus();
                }
            });
        },
        closeUnlockModal() {
            this.unlockModalOpen = false;
            this.selectedItem = null;
            this.unlockCodeDigits = ['', '', '', ''];
            this.unlockError = '';
            this.unlockSubmitting = false;
            this.activeVerifyRequestSeq = 0;
        },
        requestDelete(item) {
            if (!item || !item.id || !item.can_delete) {
                return;
            }

            this.pendingDeleteItem = item;
            this.deleteNoteModalOpen = true;
        },
        closeDeleteModal() {
            this.deleteNoteModalOpen = false;
            this.pendingDeleteItem = null;
        },
        handleCodeInput(index, event) {
            if (this.unlockSubmitting) {
                return;
            }
            const rawValue = event && event.target ? String(event.target.value || '') : '';
            const digits = rawValue.replace(/\D/g, '');
            if (!digits) {
                this.unlockCodeDigits[index] = '';
                return;
            }

            if (digits.length > 1) {
                this.applyPastedCode(digits);
                return;
            }

            this.unlockError = '';
            this.unlockCodeDigits[index] = digits;
            if (index < this.unlockCodeDigits.length - 1) {
                this.focusCodeInput(index + 1);
            }
            this.maybeAutoConfirmUnlock();
        },
        handleCodeKeydown(index, event) {
            const key = event && event.key ? event.key : '';
            if (key === 'Backspace' && !this.unlockCodeDigits[index] && index > 0) {
                this.focusCodeInput(index - 1);
            }
            if (key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                this.focusCodeInput(index - 1);
            }
            if (key === 'ArrowRight' && index < this.unlockCodeDigits.length - 1) {
                event.preventDefault();
                this.focusCodeInput(index + 1);
            }
        },
        handleCodePaste(event) {
            if (this.unlockSubmitting) {
                return;
            }
            const pastedText = event && event.clipboardData ? event.clipboardData.getData('text') : '';
            const digits = String(pastedText || '').replace(/\D/g, '');
            if (!digits) {
                return;
            }
            event.preventDefault();
            this.applyPastedCode(digits);
        },
        applyPastedCode(digits) {
            const normalized = String(digits || '').slice(0, 4);
            this.unlockError = '';
            for (let i = 0; i < this.unlockCodeDigits.length; i += 1) {
                this.unlockCodeDigits[i] = normalized[i] || '';
            }

            const focusIndex = Math.min(normalized.length, this.unlockCodeDigits.length - 1);
            this.focusCodeInput(focusIndex);
            this.maybeAutoConfirmUnlock();
        },
        maybeAutoConfirmUnlock() {
            if (this.unlockSubmitting) {
                return;
            }

            if (this.unlockCodeDigits.some((digit) => digit === '')) {
                return;
            }

            this.confirmUnlock();
        },
        focusCodeInput(index) {
            this.$nextTick(() => {
                const inputs = this.$refs.unlockCodeInput || [];
                if (inputs[index]) {
                    inputs[index].focus();
                    inputs[index].select();
                }
            });
        },
        enteredUnlockCode() {
            return this.unlockCodeDigits.join('');
        },
        sendUnlockCodeByEmail() {
            if (this.sendingUnlockCode || this.unlockCodeCooldownRemaining > 0) {
                return;
            }

            const self = this;
            this.sendingUnlockCode = true;
            this.unlockError = '';
            const payload = {};
            if (this.selectedItem && this.selectedItem.id) {
                payload.vault_id = this.selectedItem.id;
            } else if (this.paginatedItems.length > 0 && this.paginatedItems[0].id) {
                payload.vault_id = this.paginatedItems[0].id;
            }

            axios.post(this.apiVaultBasePath + '/send-access-code', {
                ...payload
            }).then(function (response) {
                const cooldownSeconds = Number(response.data && response.data.cooldown_seconds ? response.data.cooldown_seconds : 30);
                self.unlockCodeCooldownUntil = Date.now() + (cooldownSeconds * 1000);
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data && response.data.message
                        ? response.data.message
                        : (self.currentUserEmail
                            ? ('Einmal-Code wurde an ' + self.currentUserEmail + ' gesendet.')
                            : 'Einmal-Code wurde per E-Mail gesendet.'),
                    type: 'success'
                });
            }).catch(function (error) {
                const cooldownSeconds = Number(
                    error && error.response && error.response.data && error.response.data.cooldown_seconds
                        ? error.response.data.cooldown_seconds
                        : 0
                );
                if (cooldownSeconds > 0) {
                    self.unlockCodeCooldownUntil = Date.now() + (cooldownSeconds * 1000);
                }
                self.unlockError = self.getApiErrorMessage(error, self.$t('Code could not be sent').toString());
            }).finally(function () {
                self.sendingUnlockCode = false;
            });
        },
        confirmUnlock() {
            if (this.unlockSubmitting || this.activeVerifyRequestSeq !== 0) {
                return;
            }
            const enteredCode = this.enteredUnlockCode();
            if (!enteredCode || enteredCode.length !== 4) {
                return;
            }

            const self = this;
            this.unlockSubmitting = true;
            this.unlockError = '';
            const requestSeq = ++this.verifyRequestSeq;
            this.activeVerifyRequestSeq = requestSeq;

            const payload = {
                code: enteredCode
            };
            if (this.selectedItem && this.selectedItem.id) {
                payload.vault_id = this.selectedItem.id;
            }

            axios.post(this.apiVaultBasePath + '/verify-access-code', payload).then(function () {
                if (self.activeVerifyRequestSeq !== requestSeq) {
                    return;
                }
                const selectedItem = self.selectedItem;
                if (selectedItem && selectedItem.id) {
                    self.items = self.items.map(function (vaultItem) {
                        if (Number(vaultItem.id) !== Number(selectedItem.id)) {
                            return vaultItem;
                        }

                        return {
                            ...vaultItem,
                            is_unlocked: true,
                        };
                    });
                }
                self.activeVerifyRequestSeq = 0;
                self.closeUnlockModal();
                if (selectedItem) {
                    const target = self.itemManageLink(selectedItem);
                    self.$router.push(target);
                }
            }).catch(function (error) {
                if (self.activeVerifyRequestSeq !== requestSeq || !self.unlockModalOpen) {
                    return;
                }
                self.unlockCodeDigits = ['', '', '', ''];
                self.focusCodeInput(0);
                const remainingAttempts = Number(
                    error && error.response && error.response.data && error.response.data.remaining_attempts
                        ? error.response.data.remaining_attempts
                        : NaN
                );
                if (!Number.isNaN(remainingAttempts)) {
                    self.unlockError = self.$t('Code is invalid (:attempts attempts left)', {attempts: remainingAttempts}).toString();
                    return;
                }
                self.unlockError = self.getApiErrorMessage(error, self.$t('The entered code is invalid').toString());
            }).finally(function () {
                if (self.activeVerifyRequestSeq === requestSeq) {
                    self.unlockSubmitting = false;
                    self.activeVerifyRequestSeq = 0;
                }
            });
        },
        getApiErrorMessage(error, fallback) {
            if (error && error.response && error.response.data) {
                const responseData = error.response.data;
                if (responseData.message) {
                    return responseData.message;
                }
                if (responseData.errors && typeof responseData.errors === 'object') {
                    const firstField = Object.keys(responseData.errors)[0];
                    if (firstField && responseData.errors[firstField] && responseData.errors[firstField][0]) {
                        return responseData.errors[firstField][0];
                    }
                }
            }

            return fallback;
        },
        deleteSelectedVault() {
            if (!this.pendingDeleteItem || !this.pendingDeleteItem.id) {
                this.closeDeleteModal();
                return;
            }

            const self = this;
            const deleteItemId = Number(this.pendingDeleteItem.id);
            self.loading = true;
            self.deleteNoteModalOpen = false;

            axios.delete(self.apiVaultBasePath + '/' + deleteItemId).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data && response.data.message ? response.data.message : self.$t('Note was deleted').toString(),
                    type: 'success'
                });
                self.pendingDeleteItem = null;
                self.selectedItem = null;
                self.refreshItems();
            }).catch(function (error) {
                self.loading = false;
                self.deleteNoteModalOpen = true;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.getApiErrorMessage(error, self.$t('Note could not be deleted').toString()),
                    type: 'error'
                });
            });
        },
    }
}
</script>

<style scoped>
.vault-view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 1.75rem;
    width: 1.75rem;
    color: var(--ui-text-default);
    border: 0;
    background: transparent;
    border-radius: 0.25rem;
}

.vault-view-btn-active {
    color: var(--ui-text-subtle);
    cursor: default;
}

.vault-view-btn:focus,
.vault-view-btn:focus-visible,
.vault-view-btn:active {
    outline: none !important;
    box-shadow: none !important;
}

.vault-header-category-cell {
    flex: 0 0 52px;
    width: 52px;
}

.vault-header-category-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    padding: 0;
}

.vault-list-category-dot {
    display: inline-flex;
    width: 0.625rem;
    min-width: 0.625rem;
    max-width: 0.625rem;
    height: 0.625rem;
    min-height: 0.625rem;
    max-height: 0.625rem;
    border-radius: 9999px;
}

.vault-category-tag-icon {
    color: var(--ui-text-secondary);
}

.table-listview-l-th.vault-list-sticky-th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: var(--ui-surface-raised);
    white-space: nowrap;
}

.vault-list-table {
    min-width: 100%;
    border-bottom: 1px solid var(--border-ultralight);
}

.vault-tile-item {
    position: relative;
    overflow: visible;
}

.vault-tile-item:hover,
.vault-tile-item:focus-within {
    z-index: 30;
}

.vault-tile-header {
    height: 11.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.vault-title-tooltip {
    position: relative;
    z-index: 5;
}

.vault-info-tooltip-content {
    display: inline-block;
    width: max-content;
    max-width: 16rem;
    text-align: left;
    white-space: normal;
    word-break: break-word;
}

.vault-tile-title-row {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    min-width: 0;
}

.vault-tile-title-text {
    display: block;
    max-width: 12rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.95rem;
    color: var(--ui-text-strong);
}

.vault-icon-box {
    width: 1rem;
    height: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.vault-category-row {
    min-height: 2.25rem;
}

.vault-category-row-content {
    padding: 0.5rem 0.75rem;
}

.vault-category-row-dot {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
}

.vault-category-row-actions {
    width: 2.75rem;
    min-width: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.vault-category-delete-btn {
    opacity: 0;
}

.vault-category-row:hover .vault-category-delete-btn {
    opacity: 1;
}

.vault-otp-group {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
}

.vault-otp-input {
    height: 3rem;
    border: 1px solid var(--ui-border-default);
    border-left: 0;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--ui-text-strong);
    outline: none;
    border-radius: 0 !important;
}

.vault-otp-input:first-child {
    border-left: 1px solid var(--ui-border-default);
    border-top-left-radius: 0.375rem !important;
    border-bottom-left-radius: 0.375rem !important;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.vault-otp-input:not(:first-child):not(:last-child) {
    border-radius: 0 !important;
}

.vault-otp-input:last-child {
    border-top-right-radius: 0.375rem !important;
    border-bottom-right-radius: 0.375rem !important;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.vault-otp-input:focus {
    border-color: var(--ui-text-subtle);
    position: relative;
    z-index: 1;
}

@media (min-width: 1400px) {
.vault-tile-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}
}

@media (min-width: 1800px) {
.vault-tile-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
}
}
</style>
